/**
 * core/rival — the pure, deterministic heart of the Warm Rival arc (rival-arc.md).
 *
 * A single pinned regular becomes "your first friend on the circuit": they mirror your
 * starter (the classic type-triangle), thread the Badge Circuit ALWAYS one step behind
 * (never ahead — the no-"everyone's-beaten-you" guarantee), and turn up to cheer you on.
 *
 * This module owns only the maths + authored line banks — no rendering, no IO, no session
 * state — so it is exhaustively unit-testable. The session layer (session/rival.ts) mints
 * real PokemonInstances from the party SPEC these helpers produce.
 *
 * WARMTH IS LOAD-BEARING: every line here roots for the player. Nothing gloats, ranks, or
 * implies you're behind. A rival "milestone" is framed as happy-for-both-of-you; a spar
 * loss is "the best match we've had". See the no-pressure audit in rival-arc.md.
 */

/** The classic starter type-triangle: the rival "picks the one with the edge" on yours. */
export const STARTER_TRIANGLE: Readonly<Record<string, string>> = {
  bulbasaur: 'charmander', // grass ← fire
  charmander: 'squirtle', // fire ← water
  squirtle: 'bulbasaur', // water ← grass
};

/** The warm default when the player's starter isn't a standard trio member. */
export const DEFAULT_RIVAL_STARTER = 'eevee';

/**
 * The rival's mirrored starter (base species). Grass→fire, fire→water, water→grass — a
 * nostalgic nod, kept fair by the cozy difficulty dial + level-matching (never a
 * counter-pick beatdown). Any non-trio starter → a warm Eevee.
 */
export function mirrorStarter(playerStarterId: string): string {
  return STARTER_TRIANGLE[playerStarterId] ?? DEFAULT_RIVAL_STARTER;
}

/**
 * The rival trails you by exactly one badge, never leads. Clamped at 0 so they can never
 * pass you — the load-bearing "always just behind, always glad of it" guarantee.
 */
export function rivalBadgesForPlayer(playerBadges: number): number {
  return Math.max(0, Math.floor(playerBadges) - 1);
}

/**
 * A fun, close spar level tracking the player's lead mon (never a stomp either way).
 * The Cozy↔Spicy dial keeps the actual battle relaxed on top of this.
 */
export function rivalSparLevel(playerLeadLevel: number): number {
  return Math.max(5, Math.min(100, Math.floor(playerLeadLevel)));
}

// ── party spec (evolving ace + warm companions) ──────────────────────────────────

/** One step of a starter's level-up evolution line (base has minLevel 0). */
export interface AceLineStep {
  readonly speciesId: string;
  readonly minLevel: number;
}

/** A single {speciesId, level} the session mints into a real instance. */
export interface RivalPartyMember {
  readonly speciesId: string;
  readonly level: number;
}

/**
 * Which form of the mirrored starter the rival's ace has reached at a given level —
 * walk the evolution line, taking the last form whose minLevel is satisfied. Pure:
 * the caller supplies the line (built from species.evolutions) so core needs no dataset.
 */
export function pickAceForm(line: readonly AceLineStep[], level: number): string {
  let form = line[0]?.speciesId ?? '';
  for (const step of line) {
    if (level >= step.minLevel) form = step.speciesId;
  }
  return form;
}

/**
 * Build the rival's party SPEC for a spar: their evolving mirrored-starter ace at the
 * player-matched level, plus 1–2 warm companions drawn from a pool (their home biome /
 * own team), deterministic per badge tier. No RNG — same inputs, same team, so a rematch
 * is stable. The session mints these into faithful instances (real stats + PP).
 */
export function buildRivalPartySpec(args: {
  readonly aceLine: readonly AceLineStep[];
  readonly leadLevel: number;
  readonly companionPool: readonly string[];
  readonly badgeTier: number;
}): RivalPartyMember[] {
  const level = rivalSparLevel(args.leadLevel);
  const ace = pickAceForm(args.aceLine, level);
  const party: RivalPartyMember[] = [];
  if (ace) party.push({ speciesId: ace, level });

  const pool = args.companionPool.filter((id) => id && id !== ace);
  const tier = Math.max(0, Math.floor(args.badgeTier));
  const wanted = pool.length === 0 ? 0 : tier >= 3 ? 2 : 1;
  const compLevel = Math.max(5, level - 2);
  for (let i = 0; i < wanted && i < pool.length; i++) {
    const pick = pool[(tier + i) % pool.length]!;
    if (!party.some((m) => m.speciesId === pick)) {
      party.push({ speciesId: pick, level: compLevel });
    }
  }
  // Defensive: never hand back an empty party (the ace line was empty AND no pool).
  if (party.length === 0 && pool.length > 0) {
    party.push({ speciesId: pool[0]!, level });
  }
  return party;
}

// ── authored line banks + greeting selection ─────────────────────────────────────

/** The warm cameo/spar moments the rival speaks at. */
export type RivalLinePhase =
  | 'pre-badge' // pep talk before your gym challenge
  | 'post-badge' // congrats after you earn the badge
  | 'spar-offer' // offering a no-stakes friendly round
  | 'spar-win' // you beat them (they're delighted)
  | 'spar-loss' // you lost (they celebrate the match anyway)
  | 'callback'; // "remember when your {pmon}…"

/**
 * Authored FLOOR lines (the LLM phrases richer). Tokens: {n} player badge count,
 * {rivalmon} the rival's ace species, {pmon} a player mon, {memory} a remembered beat.
 * Every line is warm-by-construction — rooting for you, never gloating, never ranking.
 */
export const RIVAL_LINES: Readonly<Record<RivalLinePhase, readonly string[]>> = {
  'pre-badge': [
    "You've so got this one — I could tell the moment you walked in. Go show 'em!",
    "Deep breath — you and me both know how ready you are. I'll be right here cheering.",
    'Ooh, this is your gym, I can feel it. Go on, I want front-row seats to the win!',
  ],
  'post-badge': [
    "YES! Knew it! That's {n} for you — I'm right behind you, and honestly? Couldn't be happier chasing a friend like you.",
    'Badge number {n}! I actually cheered out loud, sorry. So proud to be walking this circuit next to you.',
    "There it is! You make it look easy. I'm a step back and loving every minute of the chase.",
  ],
  'spar-offer': [
    "No stakes, just us — one friendly round for old times? My {rivalmon}'s been itching to say hi to your {pmon}.",
    'Fancy a friendly? Win or lose I just love battling you. My {rivalmon} could use the practice!',
    "Quick spar, you and me? No badges on the line, only bragging rights I'll happily lose.",
  ],
  'spar-win': [
    'Ahh, you got me again! That was gorgeous. Teach me that switch sometime?',
    'You beat me fair and warm — best kind of loss there is. I loved every second!',
    'Outplayed, and grinning about it! Your team just keeps getting lovelier.',
  ],
  'spar-loss': [
    "Hey — that was the best match we've had! You'll get me next time, and I can't wait.",
    'So close! Honestly the most fun I’ve had all week. Rematch whenever you like, friend.',
    'That one came down to the wire — you push me to be better. Thank you for the round!',
  ],
  callback: [
    'Still telling everyone about the time your {pmon} {memory}. Best battle of my circuit.',
    "I think about your {pmon} {memory} more than I'd like to admit. What a moment!",
    'Remember your {pmon}? {memory} — I still get chills. Glad I get to walk this with you.',
  ],
};

/**
 * Should the rival pop in with a warm cameo at this badge tier? True the first time we
 * reach a tier we haven't greeted at yet (`lastGreetedBadge` starts at -1 on pinning),
 * so a cameo fires exactly ONCE per tier — never twice on a re-entry.
 */
export function shouldRivalGreet(lastGreetedBadge: number, currentTier: number): boolean {
  return currentTier > lastGreetedBadge;
}

/**
 * Pick a line from a phase's bank deterministically (seed = tier / spar count), so the
 * same moment always reads the same and successive moments vary. Returns the raw
 * template; fill tokens with {@link fillRivalLine}.
 */
export function pickRivalLine(phase: RivalLinePhase, seed: number): string {
  const bank = RIVAL_LINES[phase];
  if (bank.length === 0) return '';
  const idx = ((Math.floor(seed) % bank.length) + bank.length) % bank.length;
  return bank[idx]!;
}

/** Substitute the warm tokens into a rival line. Absent tokens fall back gently. */
export function fillRivalLine(
  line: string,
  tokens: {
    readonly n?: number;
    readonly rivalmon?: string;
    readonly pmon?: string;
    readonly memory?: string;
  } = {},
): string {
  return line
    .replace(/\{n\}/g, tokens.n != null ? String(tokens.n) : 'another')
    .replace(/\{rivalmon\}/g, tokens.rivalmon ?? 'partner')
    .replace(/\{pmon\}/g, tokens.pmon ?? 'team')
    .replace(/\{memory\}/g, tokens.memory ?? 'pulled off something wonderful');
}
